import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Button } from '../../ui/button';
import { Loader } from '../../ui/loader';

import { CustomContainer } from '../../common';
import { ArticleLink, UserLink } from '../../Links';
import { formatDatetime } from '../../Utils';

// since semantic-ui doesn't support function type in Dropdown value, we have to use string
const SORT_STRATEGIES_TYPE = {
  HOTTEST: 'hottest',
  LATEST: 'latest',
  EARLIEST: 'earliest',
};

interface Reply {
  id: string;
  title: string;
  author_id: string;
  datetime: string;
  num_pushes: number;
}

const SORT_STRATEGIES = {
  [SORT_STRATEGIES_TYPE.HOTTEST]: (a: Reply, b: Reply) => b.num_pushes - a.num_pushes,
  [SORT_STRATEGIES_TYPE.LATEST]: (a: Reply, b: Reply) => dayjs(a.datetime).isAfter(dayjs(b.datetime)) ? -1 : 1,
  [SORT_STRATEGIES_TYPE.EARLIEST]: (a: Reply, b: Reply) => dayjs(a.datetime).isBefore(dayjs(b.datetime)) ? -1 : 1,
};

const SORT_BY_DROPDOWN_OPTIONS = [
  {
    key: 'hot',
    text: '關注度',
    value: SORT_STRATEGIES_TYPE.HOTTEST,
    content: '關注度',
  },
  {
    key: 'new',
    text: '最新',
    value: SORT_STRATEGIES_TYPE.LATEST,
    content: '最新',
  },
  {
    key: 'old',
    text: '最舊',
    value: SORT_STRATEGIES_TYPE.EARLIEST,
    content: '最舊',
  },
];

interface RepliesProps {
  data: {
    replies?: Reply[];
  };
  loading: boolean;
  isMobile: boolean;
}

export default ({ data, loading, isMobile }: RepliesProps) => {
  if (!loading && (!Array.isArray(data.replies) || !data.replies.length)) {
    return null;
  }

  const [activePage, setActivePage] = useState(1);
  const [sortBy, setSortBy] = useState<string>(SORT_STRATEGIES_TYPE.HOTTEST);

  const totalPages = useMemo(
    () => Array.isArray(data.replies) && data.replies ? Math.ceil(data.replies.length / 10) : 0,
    [data.replies],
  );

  const replies = useMemo(
    () => Array.isArray(data.replies) && data.replies
      ? data.replies
          .sort(SORT_STRATEGIES[sortBy as keyof typeof SORT_STRATEGIES])
          .slice((activePage - 1) * 10, activePage * 10)
      : [],
    [data.replies, sortBy, activePage],
  );

  useEffect(() => {
    setActivePage(1);
  }, [sortBy])

  return (
    <CustomContainer>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={isMobile ? 'text-xl' : 'text-2xl'}>
              相關回文 ({data.replies?.length || 0})
            </CardTitle>
            <Select
              value={sortBy}
              onValueChange={(value) => setSortBy(value)}
            >
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_BY_DROPDOWN_OPTIONS.map((option) => (
                  <SelectItem key={option.key} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : (
            <>
              <div className="divide-y">
                {replies.map((article) => (
                  <div key={article.id} className="py-3 flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`font-semibold ${isMobile ? 'text-sm' : 'text-base'}`}>
                        <ArticleLink id={article.id}>
                          {article.title}
                        </ArticleLink>
                      </h4>
                      <div className="text-sm text-gray-600 mt-1">
                        <UserLink id={article.author_id}>
                          {article.author_id}
                        </UserLink>
                        {' @ '}
                        {formatDatetime(article.datetime)}
                      </div>
                    </div>
                    <div className="text-center ml-4">
                      <div className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                        {article.num_pushes}
                      </div>
                      <div className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                        關注度
                        <div className="flex items-center">
                          <ThumbsUp className="h-3 w-3 text-blue-600" />
                          <ThumbsDown className="h-3 w-3 text-red-600" />
                          <ArrowRight className="h-3 w-3 text-yellow-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center gap-1 mt-4">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={activePage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActivePage(page)}
                      className={isMobile && Math.abs(page - activePage) > 1 ? 'hidden' : ''}
                    >
                      {page}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </CustomContainer>
  );
};